import React, { useContext, createContext, PropsWithChildren, memo } from 'react';
import { View, Text, ImageBackgroundProps, TextProps, ScrollViewProps, Animated, FlatListProps, ViewProps } from 'react-native';
import Image, { IKwaiImageProps } from '@kibt/meta-ui-krn/lib/components/Image';
import { rtlCovertFn, rtlDirectionFn } from '@/common/rn';
import { useLogAttribute } from '@/common/hooks';
import { isWeb } from '@/common/device-info';
import { $isRtl } from '@/common/i18n';
import isEqual from 'lodash/isEqual';
import { FlatList, ScrollView } from '../GestureHandler';
import { KeyboardAwareScrollView, KeyboardAwareScrollViewProps } from 'react-native-keyboard-aware-scroll-view';
import { Swiper, SwiperProps } from '@kibt/meta-ui-krn';
import withImageWrapper from '@/common/perf/withImageWrapper';
import { HorizontalGestureConflictView } from '../HorizontalGestureConflictView';
import { useSlideBack } from '@/hooks/useSlideBack';

export const PageContext = createContext({ Styles: {} });

function rtlClassNameFn(name: string | string[], n: any) {
    if (Array.isArray(name)) {
        const result = [
            name.map(className => {
                return n[className];
            }),
            name.map(className => {
                return n[`${className}-rtl`];
            }),
        ];
        return result;
    } else {
        return [n[name], n[`${name}-rtl`]];
    }
}
/**
 *
 * @props :
 * className H5样式属性
 * style  Krn样式属性(编译后)
 * rtlClassName 包含Krn构建时产出rtl样式，依据rtl判断生效对应样式
 * rtl 直接设置direction属性
 */
const useRTLContainer = props => {
    const { className, style, rtlClassName, rtl, ...extra } = props;
    const { Styles } = useContext(PageContext);
    let rtlClassNameData = rtlClassName;
    if (rtlClassName && Styles) {
        rtlClassNameData = rtlClassNameFn(rtlClassName, Styles);
    }
    let langStyle = className;
    if (rtlClassNameData) {
        langStyle = rtlClassNameData && rtlClassNameData[0];
    }
    useLogAttribute(props.vLog);
    const classProps = {
        style: rtl ? { ...style, direction: rtl } : style,
        className: langStyle,
    };
    return { classProps, extra };
};

interface RtlProps {
    style?: any;
    className?: string;
    rtlClassName?: string | string[];
    rtl?: string;
    vLog?: any;
}

interface imageUri {
    uri: string;
}

interface KImageProps extends RtlProps {
    source: imageUri;
}

const RtlImage: React.FC<KImageProps & IKwaiImageProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <Image ref={ref} {...classProps} {...extra}>
            {props.children}
        </Image>
    );
});

export const KImage = memo(withImageWrapper(RtlImage), isEqual);
KImage.displayName = 'KImage';

const RtlImageBackground: React.FC<RtlProps & ImageBackgroundProps> = props => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <Image {...classProps} {...extra}>
            {props.children}
        </Image>
    );
};

export const KImageBackground = memo(withImageWrapper(RtlImageBackground));
KImageBackground.displayName = 'KImageBackground';

export const KView: React.FC<RtlProps | ViewProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <View ref={ref} {...classProps} {...extra}>
            {props.children}
        </View>
    );
});

export const KScrollView: React.FC<RtlProps & ScrollViewProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    // 处理容器右滑返回的问题
    const { horizontal = false } = props;

    const scrollViewNode = (
        <ScrollView ref={ref} {...classProps} {...extra}>
            {props.children}
        </ScrollView>
    );

    return horizontal ? <HorizontalGestureConflictView>{scrollViewNode}</HorizontalGestureConflictView> : scrollViewNode;
});
KScrollView.displayName = 'KScrollView';

export const KText: React.FC<RtlProps & TextProps> = React.forwardRef((props, ref) => {
    const { classProps, extra } = useRTLContainer(props);
    return (
        <Text ref={ref} {...classProps} {...extra}>
            {props.children}
        </Text>
    );
});

export const KSwiper = React.forwardRef((props: SwiperProps, ref) => {
    // 处理容器右滑返回的问题
    const { setSlideBack } = useSlideBack();
    const slideBackProps = {
        onTouchStart: evt => {
            setSlideBack(false);
            (props as any).onTouchStart?.(evt);
        },
        onTouchCancel: evt => {
            setSlideBack(true);
            (props as any).onTouchCancel?.(evt);
        },
        onTouchEnd: evt => {
            setSlideBack(true);
            (props as any).onTouchEnd?.(evt);
        },
    };
    return <Swiper ref={ref} {...props} {...slideBackProps} />;
});
KScrollView.displayName = 'KSwiper';

export const KFlatList = React.forwardRef((props: FlatListProps, ref) => {
    const { horizontal = false } = props;
    const flatListNode = <FlatList ref={ref} {...props} />;
    // 处理容器右滑返回的问题
    return horizontal ? <HorizontalGestureConflictView>{flatListNode}</HorizontalGestureConflictView> : flatListNode;
});
KFlatList.displayName = 'KFlatList';

/**
 * 技术方案 https://docs.corp.kuaishou.com/k/home/VRnhDphNUJtY/fcAAgc0pQfFd-Ct0QfCzHz6w7
 */
export function withWrapper<T extends object>(WrappedComponent) {
    return React.forwardRef((props: T & RtlProps & PropsWithChildren<any>, ref) => {
        const { classProps, extra } = useRTLContainer(props);
        return <WrappedComponent ref={ref} nativeID={'draggable_scroll_view_id'} {...classProps} {...extra} {...props} />;
    });
}

export const KDraggableScrollView = withWrapper<ScrollViewProps>(KScrollView);
export const KDraggableFlatList = withWrapper(KFlatList) as any as typeof FlatList;
export const KDraggableAnimatedFlatList = withWrapper(Animated.FlatList) as any as typeof Animated.FlatList;
export const KDraggableKeyboardAwareScrollView = withWrapper<KeyboardAwareScrollViewProps>(KeyboardAwareScrollView);
export const KDraggableAnimatedScrollView = withWrapper(Animated.ScrollView) as typeof Animated.ScrollView;
